import { Injectable } from '@angular/core';
import { GlobalService } from 'app/global/global.service';
import { Field } from 'app/interfaces/fields';
import { clearCalculations, onSaveCalculation } from 'app/global/calculation';
import { FieldTypeEnum } from 'app/interfaces/fields.dto';

@Injectable({ providedIn: 'root' })
export class CalculationFieldsService {
  constructor(private globalService: GlobalService) {}

/**
 * Retrieves a conditional field by its UUID from the global fields data.
 *
 * @param uuid - The unique identifier of the field to retrieve. If `null`, the function returns `null`.
 * @returns The corresponding `Field` object if found; otherwise, `null`.
 */
  prepareConditionalFields(uuid: string | null): Field | null {
    if (!uuid) return null;
    return this.globalService.fields_data[uuid] || null;
  }

/**
 * Updates the 'enable_calculation' property of a field identified by its UUID.
 *
 * @param uuid - The unique identifier of the field to update.
 * @param enable - The value to set for the 'enable_calculation' property (typically 0 or 1).
 */
  updateEnableCalculation(uuid: string, enable: number) {
    this.globalService.updateField(uuid, { enable_calculation: enable });
    const dragField = document.querySelector(`.drag-fields[uuid="${uuid}"]`) as HTMLElement;
    const textFieldInput = dragField?.querySelector('.text-field-input') as HTMLElement;
    this.globalService.toggleClass(textFieldInput, 'pointer-none');
    this.globalService.toggleClass(textFieldInput, 'enable-calculation');
  }

/**
 * Removes a calculation associated with the specified UUID.
 * 
 * This method first clears the calculation using `clearCalculation`, 
 * then updates the corresponding field in the global service by 
 * resetting calculation-related properties to empty strings.
 *
 * @param uuid - The unique identifier of the calculation to remove.
 */
  removeCalculation(uuid: string) {
    this.clearCalculation(uuid);
    this.globalService.updateField(uuid, { 
      eval_calculation: '', 
      const_eval_calculation: '', 
      calculation: '' 
    });
  }

/**
 * Clears calculation data for a specific field identified by its UUID.
 *
 * @param uuid - The unique identifier of the field whose calculation data should be cleared.
 */
  clearCalculation(uuid: string) {
    const fields = this.globalService.fields_data;
    clearCalculations(fields, uuid, this.globalService);
  }

/**
 * Saves a calculation for a given UUID.
 *
 * @param uuid - The unique identifier for the calculation.
 * @param calculation - The calculation string to be saved.
 */
  saveCalculation(uuid: string, calculation: string) {
    onSaveCalculation(uuid, calculation, this.globalService);
  }

/**
 * Updates the decimal value for a calculation field identified by its UUID.
 * 
 * This method updates the field's decimal value using the global service,
 * then triggers a 'keyup' event on the corresponding input element in the DOM
 * to ensure any listeners or bindings are notified of the change.
 *
 * @param uuid - The unique identifier of the calculation field to update.
 * @param decimalValue - The new decimal value to set for the field.
 */
  updateDecimal(uuid: string, decimalValue: string) {
    this.globalService.updateField(uuid, { calculation_decimal: decimalValue });
    const dragFieldInput = document.querySelector(`.drag-fields[uuid="${uuid}"] input`);
    dragFieldInput?.dispatchEvent(new Event('keyup'));
  }

/**
 * Finds and returns an array of fields that match the given mention text, excluding the field with the specified UUID.
 *
 * Iterates over all fields in `globalService.fields_data`, filtering for fields of type 'text' that:
 * - Have a different UUID than `currentUuid`
 * - Are present in the DOM with a `.drag-fields` element matching their UUID
 * - Contain the `mentionText` within their `select_type_text` property
 *
 * @param mentionText - The text to match against each field's `select_type_text`.
 * @param currentUuid - The UUID of the current field to exclude from the results.
 * @returns An array of `Field` objects that match the criteria.
 */
  findMatchingFields(mentionText: string, currentUuid: string): Field[] {
    const fields: Field[] = [];
    const query = mentionText.toLowerCase(); 
    for (const key in this.globalService.fields_data) {
      if (this.globalService.fields_data[key] && key !== 'blocks') {
        const field: Field = this.globalService.fields_data[key];
        if (
          [FieldTypeEnum.Text, FieldTypeEnum.List, FieldTypeEnum.Droplist].includes((field.type as FieldTypeEnum)) &&
          field.uuid !== currentUuid &&
          document.querySelector(`.drag-fields[uuid="${field.uuid}"]`) &&
          field.select_type_text.toLowerCase().includes(query)
        ) {
          fields.push(field);
        }
      }
    }
    return fields;
  }
}
